"use strict";
/* eslint arrow-body-style: ["error", "always"] */
/* implicit returns that reference process.env can result in invalid syntax
after code has been transpiled, depending on the tool used for transpiling.
therefore, it is safer to avoid implicit returns, hence why the eslint rule
above is enabled for this file. DO NOT REMOVE this rule unless you fully
understand the implications of doing so, across all of the apps that use
this module */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMissingConfigurationLogger = exports.setMissingConfigurationLogger = void 0;
/**
 * Provides application settings similiar to how settings work in ASP.NET Core.
 * Currently application settings are specified at build time using environment variables.
 * You can test behaviour locally using an .env.local file.
 * Within Azure DevOps build pipelines, variables must be defined as not secret to work.
 * @see https://create-react-app.dev/docs/adding-custom-environment-variables
 * Note that getRudderstackWriteKey also checks for variables prefixed with NEXT_PUBLIC_
 * This is because the Patient Portal app is a Next.js app which needs this prefix
 * for environment variables to be passed to the client
 * @see https://nextjs.org/docs/basic-features/environment-variables
 */
class AppSettings {
    constructor() {
        this._accurxPrNumber = "AccurxPrNumber";
        this.getBranchName = () => {
            return process.env.REACT_APP_WEB_CLIENT_BRANCH_NAME || null;
        };
        this.getWebClientAppName = () => {
            return (process.env.REACT_APP_WEB_CLIENT_API_CLIENT_NAME ||
                process.env.NEXT_PUBLIC_WEB_CLIENT_API_CLIENT_NAME ||
                null);
        };
        this.getWebClientAppVersion = () => {
            return (process.env.REACT_APP_WEB_CLIENT_API_CLIENT_VERSION ||
                process.env.NEXT_PUBLIC_WEB_CLIENT_API_CLIENT_VERSION ||
                null);
        };
        this.getWebClientSentrySettingsIsEnabled = () => {
            return this.accurxEnvironment !== "local";
        };
        this.getWebClientSentrySettingsDSN = () => {
            return this.returnValueOrNullWithLog(process.env.REACT_APP_WEB_CLIENT_SENTRY_SETTINGS_DSN, "WEB_CLIENT_SENTRY_SETTINGS_DSN");
        };
        this.getWebClientSentrySettingsEnvironment = () => {
            return this.accurxEnvironment;
        };
        this.getWebClientSentrySettingsRelease = () => {
            return this.returnValueOrNullWithLog(process.env.REACT_APP_WEB_CLIENT_SENTRY_SETTINGS_RELEASE, "WEB_CLIENT_SENTRY_SETTINGS_RELEASE");
        };
        this.getNextWebClientSentrySettingsRelease = () => {
            return this.returnValueOrNullWithLog(process.env
                .NEXT_PUBLIC_NEXTJS_APP_WEB_CLIENT_SENTRY_SETTINGS_RELEASE, "WEB_CLIENT_SENTRY_SETTINGS_RELEASE");
        };
        this.getApplicationInsightsKey = () => {
            var _a;
            switch (this.accurxEnvironment) {
                case "production":
                    return this.returnValueOrNullWithLog(process.env
                        .REACT_APP_WEB_CLIENT_PRODUCTION_PRODUCT_ANALYTICS_INSTRUMENTATION_KEY, "WEB_CLIENT_PRODUCTION_PRODUCT_ANALYTICS_INSTRUMENTATION_KEY");
                case "staging":
                    return this.returnValueOrNullWithLog(process.env
                        .REACT_APP_WEB_CLIENT_STAGING_PRODUCT_ANALYTICS_INSTRUMENTATION_KEY, "WEB_CLIENT_STAGING_PRODUCT_ANALYTICS_INSTRUMENTATION_KEY");
                case "development":
                    return this.returnValueOrNullWithLog(process.env
                        .REACT_APP_WEB_CLIENT_DEVELOPMENT_PRODUCT_ANALYTICS_INSTRUMENTATION_KEY, "WEB_CLIENT_DEVELOPMENT_PRODUCT_ANALYTICS_INSTRUMENTATION_KEY");
                case "local":
                    return ((_a = this.returnValueOrNullWithLog(process.env
                        .REACT_APP_WEB_CLIENT_LOCAL_PRODUCT_ANALYTICS_INSTRUMENTATION_KEY, "WEB_CLIENT_DEVELOPMENT_PRODUCT_ANALYTICS_INSTRUMENTATION_KEY")) !== null && _a !== void 0 ? _a : 
                    // need the original hardcoded value for backwards compatibility with older 1.18 shared lib when run locally
                    "1b427f92-9cc3-43bd-9569-0cb0d8ce0049");
                default:
                    this.logMissingEnvironmentVariable("WEB_CLIENT_PRODUCT_ANALYTICS_INSTRUMENTATION_KEY");
                    return null;
            }
        };
        this.getAppInsightsLoggingSinkKey = () => {
            var _a;
            switch (this.accurxEnvironment) {
                case "production":
                    return this.returnValueOrNullWithLog(process.env
                        .REACT_APP_PRODUCTION_LOGGING_APPLICATION_INSIGHTS_INSTRUMENTATION_KEY, "PRODUCTION_LOGGING_APPLICATION_INSIGHTS_INSTRUMENTATION_KEY");
                case "staging":
                    return this.returnValueOrNullWithLog(process.env
                        .REACT_APP_STAGING_LOGGING_APPLICATION_INSIGHTS_INSTRUMENTATION_KEY, "STAGING_LOGGING_APPLICATION_INSIGHTS_INSTRUMENTATION_KEY");
                case "development":
                    return this.returnValueOrNullWithLog(process.env
                        .REACT_APP_DEVELOPMENT_LOGGING_APPLICATION_INSIGHTS_INSTRUMENTATION_KEY, "DEVELOPMENT_LOGGING_APPLICATION_INSIGHTS_INSTRUMENTATION_KEY");
                case "local":
                    return ((_a = this.returnValueOrNullWithLog(process.env
                        .REACT_APP_LOCAL_LOGGING_APPLICATION_INSIGHTS_INSTRUMENTATION_KEY, "LOCAL_LOGGING_APPLICATION_INSIGHTS_INSTRUMENTATION_KEY")) !== null && _a !== void 0 ? _a : 
                    // need the original hardcoded value for backwards compatibility with older 1.18 shared lib when run locally
                    "1b427f92-9cc3-43bd-9569-0cb0d8ce0049");
                default:
                    this.logMissingEnvironmentVariable("LOGGING_APPLICATION_INSIGHTS_INSTRUMENTATION_KEY");
                    return null;
            }
        };
        // See comment at the top of the file for why the NEXT_PUBLIC_ keys are used here
        this.getRudderStackWriteKey = () => {
            switch (this.accurxEnvironment) {
                case "production":
                    return this.returnValueOrNullWithLog(process.env
                        .REACT_APP_WEB_CLIENT_PRODUCTION_RUDDER_STACK_WRITE_KEY ||
                        process.env
                            .NEXT_PUBLIC_NEXTJS_APP_WEB_CLIENT_PRODUCTION_RUDDER_STACK_WRITE_KEY, "WEB_CLIENT_PRODUCTION_RUDDER_STACK_WRITE_KEY");
                case "staging":
                    return this.returnValueOrNullWithLog(process.env
                        .REACT_APP_WEB_CLIENT_STAGING_RUDDER_STACK_WRITE_KEY ||
                        process.env
                            .NEXT_PUBLIC_NEXTJS_APP_WEB_CLIENT_STAGING_RUDDER_STACK_WRITE_KEY, "WEB_CLIENT_STAGING_RUDDER_STACK_WRITE_KEY");
                case "development":
                    return this.returnValueOrNullWithLog(process.env
                        .REACT_APP_WEB_CLIENT_DEVELOPMENT_RUDDER_STACK_WRITE_KEY ||
                        process.env
                            .NEXT_PUBLIC_NEXTJS_APP_WEB_CLIENT_DEVELOPMENT_RUDDER_STACK_WRITE_KEY, "WEB_CLIENT_DEVELOPMENT_RUDDER_STACK_WRITE_KEY");
                case "local":
                    return this.returnValueOrNullWithLog(process.env
                        .REACT_APP_WEB_CLIENT_LOCAL_RUDDER_STACK_WRITE_KEY ||
                        process.env
                            .NEXT_PUBLIC_NEXTJS_APP_WEB_CLIENT_LOCAL_RUDDER_STACK_WRITE_KEY, "LOCAL_RUDDER_STACK_WRITE_KEY");
                default:
                    this.logMissingEnvironmentVariable("RUDDER_STACK_WRITE_KEY");
                    return null;
            }
        };
        this.getWherebyAccurxSubdomainPageUrl = () => {
            return this.accurxEnvironment === "production"
                ? "https://nhs.whereby.com"
                : "https://accurx.whereby.com";
        };
        this.returnValueOrNullWithLog = (value, key) => {
            if (value === undefined) {
                if (this.accurxEnvironment !== "local") {
                    this.logMissingEnvironmentVariable(key);
                }
                return null;
            }
            return value;
        };
        this.getAccurxEnvironmentFromWindowLocation = () => {
            const hostname = typeof window !== "undefined" ? window.location.hostname : null;
            if (!hostname) {
                return null;
            }
            if (this.isLocal(hostname)) {
                return "local";
            }
            if (this.isDevelopment(hostname)) {
                return "development";
            }
            if (this.isStaging(hostname)) {
                return "staging";
            }
            if (!this.isProduction(hostname)) {
                this.logMissingEnvironmentVariable("ACCURX_ENVIRONMENT");
            }
            return "production";
        };
        this.logMissingEnvironmentVariable = (name) => {
            return this.missingConfigurationLogger(`No value found for application setting ${name}, add by setting environment variable at build time.`);
        };
        this.logInvalidEnvironmentVariable = (name, defaultValue) => {
            return this.missingConfigurationLogger(`Invalid value for application setting ${name}, using '${defaultValue}'.`);
        };
        this._accurxEnvironment = undefined;
        this.missingConfigurationLogger = console.info;
    }
    set accurxPrNumber(prNumber) {
        try {
            if (prNumber !== null) {
                sessionStorage.setItem(this._accurxPrNumber, prNumber);
            }
        }
        catch (_a) { }
    }
    get accurxPrNumber() {
        if (this.accurxEnvironment === "production" ||
            this._accurxEnvironment === "staging") {
            return null;
        }
        try {
            return sessionStorage.getItem(this._accurxPrNumber);
        }
        catch (_a) {
            return null;
        }
    }
    set accurxEnvironment(environment) {
        switch (environment) {
            case "production":
            case "staging":
            case "development":
            case "local":
                this._accurxEnvironment = environment;
                break;
            default:
                this.logInvalidEnvironmentVariable("ACCURX_ENVIRONMENT");
                this._accurxEnvironment = undefined;
        }
    }
    get accurxEnvironment() {
        const environment = this._accurxEnvironment ||
            this.getAccurxEnvironmentFromWindowLocation();
        if (environment) {
            return environment;
        }
        this.logMissingEnvironmentVariable("ACCURX_ENVIRONMENT");
        return "production";
    }
    isStaging(hostname) {
        const isNhsAccurxDemoHostname = hostname === "demo.accurx.nhs.uk";
        // The following can't actually exist under nhs.uk but can be tested with a hosts file entry
        const isThirdPartyStaging = hostname === "accurx.thirdpartystaging.nhs.uk";
        const hostnameEndsWithAccurxDemoDomain = /\.demo\.accurx\.com$/.test(hostname);
        return (isNhsAccurxDemoHostname ||
            isThirdPartyStaging ||
            hostnameEndsWithAccurxDemoDomain);
    }
    isDevelopment(hostname) {
        const isNhsAccurxDevHostname = hostname === "dev.accurx.nhs.uk";
        const hostnameEndsWithAccurxDevDomain = /\.dev\.accurx\.com$/.test(hostname);
        return isNhsAccurxDevHostname || hostnameEndsWithAccurxDevDomain;
    }
    isLocal(hostname) {
        return (hostname === "localhost" ||
            hostname === "127.0.0.1" ||
            hostname === "[::1]");
    }
    isProduction(hostname) {
        const isNhsAccurxProductionHostname = hostname === "accurx.nhs.uk";
        const hostnameEndsWithAccurxDomain = /\.accurx\.com$/.test(hostname);
        const hostanmeDoesNotIncludeDevDemoStaging = !/((dev|demo|staging)\.)/.test(hostname);
        return (isNhsAccurxProductionHostname ||
            (hostnameEndsWithAccurxDomain &&
                hostanmeDoesNotIncludeDevDemoStaging));
    }
}
const appSettings = new AppSettings();
// also accessible directly - exported for backwards compatibility
const setMissingConfigurationLogger = (logger) => {
    appSettings.missingConfigurationLogger = logger;
};
exports.setMissingConfigurationLogger = setMissingConfigurationLogger;
const getMissingConfigurationLogger = () => {
    return appSettings.missingConfigurationLogger;
};
exports.getMissingConfigurationLogger = getMissingConfigurationLogger;
exports.default = appSettings;
//# sourceMappingURL=AppSettings.js.map